/**
 * Usage events for the portal's Admin Engagement page: the app being opened,
 * which screen is shown, and a few actions. Queued in memory and posted in
 * small batches to the API's /engagement/events/; the API tags them as
 * companion events from the request's Origin, and records sign-ins itself.
 *
 * Fire and forget: a failed post is dropped, never retried, so tracking can
 * never slow the app or pile up requests. No cart, store or user ids go in
 * event names; they're fixed labels so the page can rank them.
 */
type EventType = 'session_start' | 'page_view' | 'card_click';
type EngagementEvent = { type: EventType; name: string };
type Sender = (events: EngagementEvent[]) => Promise<unknown>;

/** Screens, recorded as page views. */
export const SCREEN_NAMES = {
  home: 'Companion: Home',
  switcher: 'Companion: Store switcher',
  detail: 'Companion: Cart detail',
} as const;

/** Actions, recorded as card clicks (the page ranks them like cards). */
export const ACTION_NAMES = {
  openCart: 'Companion: Opened a cart',
  switchStore: 'Companion: Switched store',
  refresh: 'Companion: Pulled to refresh',
} as const;

const MAX_BATCH = 50;
const FLUSH_DELAY_MS = 5_000;

let send: Sender | null = null;
let queue: EngagementEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

export const flushEngagement = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!send || queue.length === 0) return;
  while (queue.length) {
    send(queue.splice(0, MAX_BATCH)).catch(() => {});
  }
};

/** Null stops tracking (signed out, previewing, or a host not yet released). */
export const setEngagementSender = (sender: Sender | null) => {
  send = sender;
  if (!sender) queue = [];
};

export const track = (type: EventType, name = '') => {
  if (!send) return;
  queue.push({ type, name });
  if (queue.length >= MAX_BATCH) flushEngagement();
  else if (!timer) timer = setTimeout(flushEngagement, FLUSH_DELAY_MS);
};
